import i18n from "@/i18n";
/**
 * Maps backend API message strings to i18n translation keys
 * This utility provides centralized mapping between backend messages and frontend translations
 */

// Direct mapping of backend messages to translation keys
const MESSAGE_KEY_MAP: Record<string, string> = {
  // Welcome & General
  'Welcome to the API': 'errors:api.welcome',
  
  // User Management
  'User created successfully': 'errors:api.user.created',
  'User updated successfully': 'errors:api.user.updated', 
  'User deleted successfully': 'errors:api.user.deleted',
  'User not found': 'errors:api.user.notFound',
  'User balance not found': 'errors:api.user.balanceNotFound',
  
  // Authentication
  'Invalid credentials': 'errors:auth.invalidCredentials',
  'Authorization required': 'errors:api.auth.authorizationRequired',
  'Email not verified': 'errors:auth.emailNotVerified',
  'Unauthorized': 'errors:auth.unauthorized',
  'Forbidden access': 'errors:api.auth.forbidden',
  'Admin privileges required': 'errors:api.auth.adminRequired',
  'Moderator privileges required': 'errors:api.auth.moderatorRequired',
  
  // Email Verification
  'Invalid verification token': 'errors:api.auth.invalidVerificationToken',
  'Email already verified': 'errors:api.auth.emailAlreadyVerified',
  'Email already in use': 'errors:api.auth.emailAlreadyInUse',
  'Email verified successfully': 'errors:api.auth.emailVerified',
  'Verification email sent successfully': 'errors:api.auth.verificationEmailSent',
  'Verification token expired': 'errors:api.auth.verificationTokenExpired',
  
  // Password Management
  'Password reset email sent successfully': 'errors:api.auth.passwordResetEmailSent',
  'Invalid or expired token': 'errors:api.auth.invalidPasswordResetToken',
  'Password reset successfully': 'errors:api.auth.passwordResetSuccess',
  'Passwords do not match': 'errors:api.auth.passwordMismatch',
  'Password reset token expired': 'errors:api.auth.passwordResetTokenExpired',
  
  // Token Management  
  'Invalid refresh token': 'errors:api.auth.invalidRefreshToken',
  'Refresh token has expired': 'errors:api.auth.refreshTokenExpired',
  'Token refreshed successfully': 'errors:api.auth.tokenRefreshed',
  'Refresh token revoked successfully': 'errors:api.auth.refreshTokenRevoked',
  'Your token has expired. Please log in again.': 'errors:auth.sessionExpired',
  'Token is required': 'errors:api.auth.tokenRequired',
  
  // Security
  'CSRF token required': 'errors:api.auth.csrfTokenRequired',
  'Invalid API key': 'errors:api.auth.invalidApiKey',
  
  // Server & System
  'Server error': 'errors:network.serverError',
  'Missing required fields': 'errors:validation.missingFields',
  'Config not found': 'errors:api.system.configNotFound',
  
  // Currency & Balance
  'Currency created successfully': 'errors:api.currency.created',
  'Currency not found': 'errors:api.currency.notFound',
  'Balance not found': 'errors:transaction.insufficientFunds',
  'Insufficient balance': 'errors:transaction.insufficientFunds',
  'Coin not found': 'errors:api.currency.coinNotFound',
  
  // Game Room & Rounds
  'Room not found': 'errors:room.notFound',
  'Round not found': 'errors:api.room.roundNotFound',
  'Round is already finished': 'errors:api.room.roundFinished',
  'Player already has a seat in this round': 'errors:room.alreadyJoined',
  'Round is full - no available slots': 'errors:room.full',
  
  // Payments
  'Amount must be at least': 'errors:api.payment.amountMustBeAtLeast',
  'Payment failed': 'errors:transaction.failed',
  'Payment already processed': 'errors:api.payment.alreadyProcessed',
  
  // Country Blocking
  'Access denied: Your country is restricted from using this service': 'errors:countryBlocking.message'
};

/**
 * Maps a backend API message to its corresponding i18n translation key
 * @param message - The raw message string from the API response
 * @returns The i18n translation key, or null if no mapping exists
 */
export const mapMessageToTranslationKey = (message: string): Parameters<typeof i18n.t>[0] | null => {
  // Direct lookup first
  const directKey = MESSAGE_KEY_MAP[message];
  if (directKey) {
    return directKey;
  }
  
  // Pattern matching for dynamic messages
  if (message.startsWith('Amount must be at least')) {
    return 'errors:api.payment.amountMustBeAtLeast';
  }
  
  // Return null if no mapping found
  return null;
};

/**
 * Gets all available message mappings
 * Useful for debugging and testing
 */
export const getAllMessageMappings = (): Record<string, string> => {
  return { ...MESSAGE_KEY_MAP };
};

/**
 * Checks if a message has a translation key mapping
 * @param message - The message to check
 * @returns boolean indicating if mapping exists
 */
export const hasTranslationKey = (message: string): boolean => {
  return mapMessageToTranslationKey(message) !== null;
};